/**
 * Branded PDF builder for PLANIVANCE AI planning reports.
 *
 * All exported documents share the same header, footer, disclaimer block,
 * and typography so beta users see a single consistent artifact.
 *
 * EDUCATIONAL USE ONLY — every report includes legally-conservative disclaimers.
 */
import jsPDF from 'jspdf';

const NAVY = [10, 17, 40];
const GOLD = [197, 160, 89];
const INK = [26, 32, 44];
const MUTED = [74, 85, 104];
const PAGE = { width: 210, height: 297 };
const MARGIN = 18;

const fmtMoney = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtPct = (n) => `${Math.round(Number(n || 0) * 1000) / 10}%`;

/** Draw the gold/navy report header. */
const drawHeader = (doc, subtitle) => {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE.width, 38, 'F');
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('PLANIVANCE AI', PAGE.width / 2, 18, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(248, 249, 250);
  doc.setFontSize(10);
  doc.text(subtitle, PAGE.width / 2, 27, { align: 'center' });
  doc.setFontSize(8);
  doc.setTextColor(160, 170, 191);
  doc.text(
    `Generated ${new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`,
    PAGE.width / 2,
    34,
    { align: 'center' }
  );
};

/** Draw footer + disclaimer block on every page. */
const drawFooter = (doc, pageNumber, totalPages) => {
  const y = PAGE.height - 30;
  doc.setDrawColor(226, 232, 240);
  doc.line(MARGIN, y, PAGE.width - MARGIN, y);

  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.setFont('helvetica', 'bold');
  doc.text('IMPORTANT DISCLAIMERS', MARGIN, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'Educational use only. This report does not constitute personalized investment, tax, or legal advice. Projections are deterministic',
    MARGIN, y + 9
  );
  doc.text(
    'estimates; actual results will vary with markets, tax law, healthcare costs, and longevity. Consult qualified professionals before acting.',
    MARGIN, y + 12.5
  );

  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.text('PLANIVANCE AI', PAGE.width / 2, y + 20, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  doc.text('A Product of Outsource Solution Strategies, LLC', PAGE.width / 2, y + 24.5, { align: 'center' });
  if (totalPages > 1) {
    doc.text(`Page ${pageNumber} of ${totalPages}`, PAGE.width - MARGIN, y + 24.5, { align: 'right' });
  }
};

/** Insert a styled section header. */
const sectionHeader = (doc, title, y) => {
  doc.setFillColor(247, 250, 252);
  doc.rect(MARGIN, y - 5, PAGE.width - 2 * MARGIN, 9, 'F');
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, y + 4.5, MARGIN + 4, y + 4.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text(title.toUpperCase(), MARGIN + 6, y + 1.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  return y + 12;
};

/** Two-column key/value pair list, returns next y. */
const kvList = (doc, items, y, options = {}) => {
  const colWidth = (PAGE.width - 2 * MARGIN) / 2;
  doc.setFontSize(9);
  items.forEach((item, idx) => {
    const colIndex = options.cols === 1 ? 0 : idx % 2;
    const rowIndex = options.cols === 1 ? idx : Math.floor(idx / 2);
    const x = MARGIN + colIndex * colWidth;
    const rowY = y + rowIndex * 6;
    doc.setTextColor(...MUTED);
    doc.text(item.label, x, rowY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...(item.color || INK));
    doc.text(String(item.value), x + colWidth - 4, rowY, { align: 'right' });
    doc.setFont('helvetica', 'normal');
  });
  const rows = options.cols === 1 ? items.length : Math.ceil(items.length / 2);
  return y + rows * 6 + 2;
};

/** Page-break helper: returns updated y, increments page if needed. */
const ensureSpace = (doc, y, required = 30) => {
  if (y + required > PAGE.height - 35) {
    doc.addPage();
    return MARGIN + 4;
  }
  return y;
};

/* ============================================================
 *  GAP ANALYZER REPORT
 * ============================================================ */
export const buildGapAnalysisPdf = ({ inputs, result, wealthHealth }) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  drawHeader(doc, 'Retirement Income Gap Analysis');

  let y = 46;

  // 1) Snapshot KPIs
  y = sectionHeader(doc, 'Snapshot', y);
  const b = result.baseline;
  y = kvList(doc, [
    { label: 'Current Age', value: inputs.current_age },
    { label: 'Retirement Age', value: inputs.retirement_age },
    { label: 'Life Expectancy', value: inputs.life_expectancy },
    { label: 'Today\'s Annual Spending', value: fmtMoney(inputs.current_annual_spending) },
    { label: 'Annual Spending Need at Retirement', value: fmtMoney(b.annual_spending_at_retirement) },
    { label: 'Expected Annual Income at Retirement', value: fmtMoney(b.annual_income_at_retirement) },
    {
      label: b.annual_gap > 0 ? 'Annual Shortfall' : 'Annual Surplus',
      value: fmtMoney(Math.abs(b.annual_gap)),
      color: b.annual_gap > 0 ? [197, 48, 48] : [45, 106, 79],
    },
    { label: 'Assets at Retirement', value: fmtMoney(b.assets_at_retirement) },
  ], y);

  y += 2;

  // 2) Retirement Readiness Score (Gap + WHS side by side)
  y = sectionHeader(doc, 'Readiness Scores', y);
  y = kvList(doc, [
    {
      label: 'Retirement Readiness',
      value: `${b.readiness_score}/100`,
      color: b.readiness_score >= 75 ? [45, 106, 79] : b.readiness_score >= 50 ? [183, 121, 31] : [197, 48, 48],
    },
    {
      label: 'Wealth Health Score\u2122',
      value: wealthHealth ? `${wealthHealth.total_score}/100 (${wealthHealth.letter_grade})` : 'Not available',
      color: wealthHealth && wealthHealth.total_score >= 75 ? [45, 106, 79] : [183, 121, 31],
    },
    {
      label: 'Asset Longevity',
      value: b.depletion_age
        ? `${b.years_lasting} yrs (deplete at age ${b.depletion_age})`
        : `Through age ${inputs.life_expectancy}`,
    },
    { label: 'Lifetime Tax Estimate (rough)', value: fmtMoney(b.lifetime_tax_estimate) },
  ], y);

  if (wealthHealth?.breakdown) {
    y += 2;
    y = sectionHeader(doc, 'Wealth Health Score Breakdown', y);
    const breakdown = Object.entries(wealthHealth.breakdown).map(([key, v]) => ({
      label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value: `${v.score}/${v.max} · ${v.metric}`,
    }));
    y = kvList(doc, breakdown, y);
  }

  y += 2;

  // 3) Assumptions
  y = ensureSpace(doc, y, 40);
  y = sectionHeader(doc, 'Key Assumptions', y);
  y = kvList(doc, [
    { label: 'Preset Used', value: result.preset_used.charAt(0).toUpperCase() + result.preset_used.slice(1) },
    { label: 'Inflation', value: fmtPct(result.assumptions.inflation) },
    { label: 'Pre-Retirement Return', value: fmtPct(result.assumptions.pre_return) },
    { label: 'Post-Retirement Return', value: fmtPct(result.assumptions.post_return) },
    { label: 'Social Security COLA', value: fmtPct(result.assumptions.ss_cola) },
    { label: 'Effective Tax Rate (estimate)', value: '22%' },
  ], y);

  y += 2;

  // 4) Gap closure scenarios
  y = ensureSpace(doc, y, 60);
  y = sectionHeader(doc, 'Gap Closure Opportunities', y);
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(
    'No single path is recommended. Review tradeoffs and consult a qualified planner for your circumstances.',
    MARGIN, y
  );
  y += 5;

  // Scenario table header
  const cols = [
    { label: 'Scenario', x: MARGIN, w: 56 },
    { label: 'Readiness', x: MARGIN + 56, w: 22 },
    { label: 'Gap Δ/yr', x: MARGIN + 78, w: 24 },
    { label: 'Yrs Lasting', x: MARGIN + 102, w: 22 },
    { label: 'Tax Δ', x: MARGIN + 124, w: 26 },
    { label: 'Impact', x: MARGIN + 150, w: 22 },
  ];
  doc.setFillColor(...NAVY);
  doc.rect(MARGIN, y, PAGE.width - 2 * MARGIN, 7, 'F');
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  cols.forEach((c) => doc.text(c.label, c.x + 1, y + 5));
  y += 9;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...INK);

  result.scenarios.forEach((s, idx) => {
    y = ensureSpace(doc, y, 14);
    if (idx % 2 === 0) {
      doc.setFillColor(248, 249, 250);
      doc.rect(MARGIN, y - 4, PAGE.width - 2 * MARGIN, 12, 'F');
    }
    if (s.strongest_effect) {
      doc.setFillColor(197, 160, 89);
      doc.rect(MARGIN - 1, y - 4, 1.5, 12, 'F');
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(s.label, cols[0].x + 1, y + 1, { maxWidth: cols[0].w - 2 });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(s.description, cols[0].x + 1, y + 5, { maxWidth: cols[0].w - 2 });

    doc.setTextColor(...INK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(`${s.readiness_score}%`, cols[1].x + 1, y + 1);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(s.readiness_delta >= 0 ? 45 : 197, s.readiness_delta >= 0 ? 106 : 48, s.readiness_delta >= 0 ? 79 : 48);
    doc.text(`${s.readiness_delta >= 0 ? '+' : ''}${s.readiness_delta}`, cols[1].x + 1, y + 5);

    doc.setTextColor(...INK);
    doc.setFontSize(8);
    doc.text(fmtMoney(s.gap_reduction), cols[2].x + 1, y + 3);
    doc.text(`${s.years_lasting}`, cols[3].x + 1, y + 3);
    const taxColor = s.lifetime_tax_delta < 0 ? [45, 106, 79] : [197, 48, 48];
    doc.setTextColor(...taxColor);
    doc.text(`${s.lifetime_tax_delta > 0 ? '+' : ''}${fmtMoney(s.lifetime_tax_delta)}`, cols[4].x + 1, y + 3);
    doc.setTextColor(...INK);
    doc.text(s.lifestyle_impact, cols[5].x + 1, y + 3);

    if (s.strongest_effect) {
      doc.setFontSize(6);
      doc.setTextColor(...GOLD);
      doc.text('STRONGEST EFFECT', cols[5].x + 1, y + 7);
      doc.setTextColor(...INK);
    }
    y += 13;
  });

  // Page numbers
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawFooter(doc, i, total);
  }

  return doc;
};

/* ============================================================
 *  SCENARIO PLANNER REPORT
 * ============================================================ */
export const buildScenarioPlannerPdf = ({ scenarios, results, wealthHealth }) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  drawHeader(doc, 'Goal-Based Scenario Comparison');

  let y = 46;

  y = sectionHeader(doc, 'Scenario Inputs', y);
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(`Comparing ${scenarios.length} retirement scenarios side-by-side.`, MARGIN, y);
  y += 6;

  scenarios.forEach((s, idx) => {
    y = ensureSpace(doc, y, 22);
    doc.setFillColor(...NAVY);
    doc.rect(MARGIN, y, 3, 16, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text(s.label, MARGIN + 6, y + 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(
      `Retire at ${s.retirement_age} · Balance ${fmtMoney(s.current_retirement_balance)} · Contribution ${fmtMoney(s.monthly_contribution)}/mo · Expenses ${fmtMoney(s.monthly_expenses_in_retirement)}/mo · SS ${fmtMoney(s.monthly_social_security)}/mo`,
      MARGIN + 6, y + 10
    );
    y += 18;
  });

  y += 2;

  if (results?.recommended) {
    y = ensureSpace(doc, y, 30);
    y = sectionHeader(doc, 'Recommended Path', y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...GOLD);
    doc.text(results.recommended, MARGIN, y + 2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(`Highest balance scenario: ${results.highest_balance_scenario}`, MARGIN, y + 8);
    y += 14;
  }

  if (results?.scenarios) {
    y = ensureSpace(doc, y, 40);
    y = sectionHeader(doc, 'Side-By-Side Comparison', y);
    const cols = [
      { label: 'Scenario', x: MARGIN, w: 56 },
      { label: 'Retire', x: MARGIN + 56, w: 18 },
      { label: 'Balance @ Ret', x: MARGIN + 74, w: 30 },
      { label: 'Withdraw/yr', x: MARGIN + 104, w: 30 },
      { label: 'Final Balance', x: MARGIN + 134, w: 32 },
      { label: 'Status', x: MARGIN + 166, w: 18 },
    ];
    doc.setFillColor(...NAVY);
    doc.rect(MARGIN, y, PAGE.width - 2 * MARGIN, 7, 'F');
    doc.setTextColor(...GOLD);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    cols.forEach((c) => doc.text(c.label, c.x + 1, y + 5));
    y += 9;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK);
    results.scenarios.forEach((s, idx) => {
      y = ensureSpace(doc, y, 9);
      if (idx % 2 === 0) {
        doc.setFillColor(248, 249, 250);
        doc.rect(MARGIN, y - 4, PAGE.width - 2 * MARGIN, 8, 'F');
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text(s.label, cols[0].x + 1, y, { maxWidth: cols[0].w - 2 });
      doc.setFont('helvetica', 'normal');
      doc.text(String(s.retirement_age), cols[1].x + 1, y);
      doc.text(fmtMoney(s.balance_at_retirement), cols[2].x + 1, y);
      doc.text(fmtMoney(s.annual_withdrawal_needed), cols[3].x + 1, y);
      doc.text(fmtMoney(s.final_balance), cols[4].x + 1, y);
      const statusColor = s.success_indicator === 'PASS' ? [45, 106, 79] : s.success_indicator === 'TIGHT' ? [183, 121, 31] : [197, 48, 48];
      doc.setTextColor(...statusColor);
      doc.setFont('helvetica', 'bold');
      doc.text(s.success_indicator, cols[5].x + 1, y);
      doc.setTextColor(...INK);
      doc.setFont('helvetica', 'normal');
      y += 9;
    });
  }

  if (wealthHealth) {
    y = ensureSpace(doc, y, 25);
    y = sectionHeader(doc, 'Wealth Health Score\u2122 Context', y);
    y = kvList(doc, [
      {
        label: 'Wealth Health Score',
        value: `${wealthHealth.total_score}/100 (${wealthHealth.letter_grade})`,
        color: wealthHealth.total_score >= 75 ? [45, 106, 79] : [183, 121, 31],
      },
    ], y, { cols: 1 });
  }

  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawFooter(doc, i, total);
  }

  return doc;
};
